import type { SupabaseClient } from "@supabase/supabase-js";
import { z } from "zod";

import {
  parseSupabaseFunctionError,
  SupabaseParseError,
  SupabaseResponseError,
  type ParsedSupabaseFunctionError,
  type SupabaseParsePhase,
} from "@shared/functions/supabaseFunctionError.ts";

type SupabaseFunctionsClient = {
  functions: {
    invoke: SupabaseClient["functions"]["invoke"];
  };
};

const EDGE_CONTRACT = Symbol("EDGE_CONTRACT");

export type EdgeFunctionContract<
  I extends z.ZodTypeAny,
  O extends z.ZodTypeAny,
  E extends z.ZodTypeAny | undefined = undefined,
> = {
  readonly [EDGE_CONTRACT]: true;
  readonly name: string;
  readonly inputSchema: I;
  readonly outputSchema: O;
  readonly errorSchema?: E;
};

export type AnyEdgeFunctionContract = EdgeFunctionContract<
  z.ZodTypeAny,
  z.ZodTypeAny,
  z.ZodTypeAny | undefined
>;

type ContractErrorSchema<C extends AnyEdgeFunctionContract> = C["errorSchema"];

export type ContractInput<C extends AnyEdgeFunctionContract> = z.input<
  C["inputSchema"]
>;

export type ContractOutput<C extends AnyEdgeFunctionContract> = z.output<
  C["outputSchema"]
>;

export type ContractErrorBody<C extends AnyEdgeFunctionContract> =
  ContractErrorSchema<C> extends z.ZodTypeAny
    ? z.output<NonNullable<ContractErrorSchema<C>>>
    : unknown;

type DeriveNameOptions = {
  meta: ImportMeta;
};

const deriveName = ({ meta }: DeriveNameOptions): string => {
  const url = new URL(meta.url);
  const segments = url.pathname.split("/").filter(Boolean);
  const [maybeFile, maybeParent] = [segments.at(-1), segments.at(-2)];
  return maybeFile?.replace(/\.[^.]+$/, "") ?? maybeParent ?? "unknown";
};

export const defineEdgeFunctionContract = <
  I extends z.ZodTypeAny,
  O extends z.ZodTypeAny,
  E extends z.ZodTypeAny | undefined = undefined,
>(options: {
  name: string;
  input: I;
  output: O;
  error?: E;
}): EdgeFunctionContract<I, O, E> =>
  ({
    [EDGE_CONTRACT]: true,
    name: options.name,
    inputSchema: options.input,
    outputSchema: options.output,
    ...(options.error ? { errorSchema: options.error } : {}),
  }) as const;

export const defineContractFromMeta = <
  I extends z.ZodTypeAny,
  O extends z.ZodTypeAny,
  E extends z.ZodTypeAny | undefined = undefined,
>(options: {
  meta: ImportMeta;
  input: I;
  output: O;
  error?: E;
  name?: string;
}) => {
  const base = {
    name: options.name ?? deriveName({ meta: options.meta }),
    input: options.input,
    output: options.output,
  };

  return options.error
    ? defineEdgeFunctionContract({ ...base, error: options.error })
    : defineEdgeFunctionContract(base);
};

export type InvokeFunctionOptions<C extends AnyEdgeFunctionContract> = {
  client: SupabaseFunctionsClient;
  body: ContractInput<C>;
  headers?: Record<string, string>;
};

export type InvokeSuccess<C extends AnyEdgeFunctionContract> = {
  ok: true;
  data: ContractOutput<C>;
  response?: Response;
};

export type InvokeFailure<C extends AnyEdgeFunctionContract> = {
  ok: false;
  error: ParsedSupabaseFunctionError<ContractErrorBody<C>>;
};

export type InvokeResult<C extends AnyEdgeFunctionContract> =
  | InvokeSuccess<C>
  | InvokeFailure<C>;

const getParseIssues = (cause: unknown): readonly unknown[] | undefined =>
  cause instanceof z.ZodError ? cause.issues : undefined;

const createParseFailure = <C extends AnyEdgeFunctionContract>(
  contract: C,
  phase: SupabaseParsePhase,
  cause: unknown,
): InvokeFailure<C> => {
  const message =
    phase === "input"
      ? `Invalid input for ${contract.name}`
      : phase === "output"
        ? `Invalid response from ${contract.name}`
        : `Invalid ${phase} payload for ${contract.name}`;
  const issues = getParseIssues(cause);

  return {
    ok: false,
    error: new SupabaseParseError({
      phase,
      message,
      ...(issues !== undefined ? { issues } : {}),
    }),
  } satisfies InvokeFailure<C>;
};

const hasErrorSchema = <
  I extends z.ZodTypeAny,
  O extends z.ZodTypeAny,
  E extends z.ZodTypeAny | undefined,
>(
  contract: EdgeFunctionContract<I, O, E>,
): contract is EdgeFunctionContract<I, O, NonNullable<E>> & {
  errorSchema: NonNullable<E>;
} => Boolean(contract.errorSchema);

const applyErrorSchema = <C extends AnyEdgeFunctionContract>(
  contract: C,
  parsed: ParsedSupabaseFunctionError,
): ParsedSupabaseFunctionError<ContractErrorBody<C>> => {
  if (!hasErrorSchema(contract)) {
    return parsed as ParsedSupabaseFunctionError<ContractErrorBody<C>>;
  }

  if (!(parsed instanceof SupabaseResponseError)) {
    return parsed as ParsedSupabaseFunctionError<ContractErrorBody<C>>;
  }

  if (parsed.body === undefined) {
    return parsed as ParsedSupabaseFunctionError<ContractErrorBody<C>>;
  }

  try {
    const typedBody = contract.errorSchema.parse(parsed.body) as Awaited<
      ContractErrorBody<C>
    >;
    return parsed.withBody(typedBody) as ParsedSupabaseFunctionError<
      ContractErrorBody<C>
    >;
  } catch (schemaError) {
    console.warn(
      `[edge-contract] Failed to parse error body for ${contract.name}:`,
      schemaError,
    );
    return parsed as ParsedSupabaseFunctionError<ContractErrorBody<C>>;
  }
};

export async function invokeFunction<C extends AnyEdgeFunctionContract>(
  contract: C,
  options: InvokeFunctionOptions<C>,
): Promise<InvokeResult<C>> {
  const inputResult = contract.inputSchema.safeParse(options.body);
  if (!inputResult.success) {
    return createParseFailure(contract, "input", inputResult.error);
  }
  const payload = inputResult.data as ContractInput<C>;
  const { data, error } = await options.client.functions.invoke<ContractOutput<C>>(contract.name, {
    body: payload as Record<string, unknown>,
    ...(options.headers ? { headers: options.headers } : {}),
  });

  if (error) {
    const parsed = await parseSupabaseFunctionError(error);
    console.error("PARSED ERROR:", JSON.parse(JSON.stringify(parsed)), " END");
    return {
      ok: false,
      error: applyErrorSchema(contract, parsed),
    } satisfies InvokeFailure<C>;
  }

  const outputResult = contract.outputSchema.safeParse(data);
  if (!outputResult.success) {
    return createParseFailure(contract, "output", outputResult.error);
  }

  return {
    ok: true,
    data: outputResult.data as ContractOutput<C>,
  } satisfies InvokeSuccess<C>;
}
